import { useEffect, useRef } from 'react';
import { loadYandexMaps } from '../utils/yandexMaps';

interface YandexMapProps {
  center: [number, number];
  zoom?: number;
  markers: Array<{
    id: string;
    position: [number, number];
    content: string;
    color?: 'green' | 'red';
  }>;
  style?: React.CSSProperties;
}

declare global {
  interface Window {
    ymaps: any;
  }
}

export function YandexMap({ center, zoom = 10, markers, style }: YandexMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    const initMap = async () => {
      // Загружаем Yandex Maps API, если еще не загружен
      try {
        await loadYandexMaps();
      } catch (error) {
        console.error('Ошибка загрузки Yandex Maps API:', error);
        return;
      }

      if (!window.ymaps || !mapRef.current) return;

      window.ymaps.ready(() => {
        if (!mapRef.current) return;

        // Удаляем предыдущую карту, если она существует
        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
        }

        // Создаем новую карту
        const map = new window.ymaps.Map(mapRef.current, {
          center,
          zoom,
        });

        // Добавляем маркеры
        markers.forEach((marker) => {
          const placemark = new window.ymaps.Placemark(
            marker.position,
            {
              balloonContent: marker.content,
            },
            {
              preset: marker.color === 'green' 
                ? 'islands#greenCircleIcon' 
                : marker.color === 'red'
                ? 'islands#redCircleIcon'
                : 'islands#blueCircleIcon',
            }
          );
          map.geoObjects.add(placemark);
        });

        mapInstanceRef.current = map;
      });
    };

    // Инициализируем карту
    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
      }
    };
  }, [center, zoom, markers]);

  return <div ref={mapRef} style={{ width: '100%', height: '400px', ...style }} />;
}

